// components/reports/VulnTrendChart.js
// Vulnerability Count Trend — Recharts StackedAreaChart
// Shows critical/high/medium/low vulnerability counts over time
// Uses useThemeColors() for all colors — no CSS var strings in SVG props

import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { useThemeColors } from '../../hooks/useThemeColors';

function VulnTrendChart({ data = [] }) {
  const colors = useThemeColors();

  if (!data || data.length === 0) {
    return (
      <div className="trend-chart-card">
        <h3 className="trend-chart-title">Vulnerability Trends</h3>
        <div className="trend-chart-empty">
          No vulnerability trend data available yet.
        </div>
      </div>
    );
  }

  // Determine if light theme for opacity adjustment
  const isLight = colors.bg.primary.startsWith('#F') || colors.bg.primary.startsWith('#f');
  const fillOpacity = isLight ? 0.3 : 0.15;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    const total = (d.critical || 0) + (d.high || 0) + (d.medium || 0) + (d.low || 0);

    return (
      <div style={{
        background: colors.bg.card,
        border: `1px solid ${colors.border.medium}`,
        borderRadius: '6px',
        padding: '10px 14px',
        fontSize: '13px'
      }}>
        <div style={{ color: colors.text.primary, fontWeight: 'bold', marginBottom: 6 }}>
          {d.label || label} — Total: {total}
        </div>
        <div style={{ color: colors.severity.critical }}>Critical: {d.critical || 0}</div>
        <div style={{ color: colors.severity.high }}>High: {d.high || 0}</div>
        <div style={{ color: colors.severity.medium }}>Medium: {d.medium || 0}</div>
        <div style={{ color: colors.severity.low }}>Low: {d.low || 0}</div>
      </div>
    );
  };

  return (
    <div className="trend-chart-card">
      <div className="trend-chart-header">
        <h3 className="trend-chart-title">Vulnerability Trends</h3>
        <span className="trend-chart-period">Last {data.length} weeks</span>
      </div>
      <div className="trend-chart-body">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border.dark} />
            <XAxis
              dataKey="label"
              stroke={colors.text.muted}
              tick={{ fontSize: 12, fill: colors.text.muted }}
            />
            <YAxis
              stroke={colors.text.muted}
              tick={{ fontSize: 12, fill: colors.text.muted }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="critical"
              stackId="1"
              stroke={colors.severity.critical}
              fill={colors.severity.critical}
              fillOpacity={fillOpacity}
            />
            <Area
              type="monotone"
              dataKey="high"
              stackId="1"
              stroke={colors.severity.high}
              fill={colors.severity.high}
              fillOpacity={fillOpacity}
            />
            <Area
              type="monotone"
              dataKey="medium"
              stackId="1"
              stroke={colors.severity.medium}
              fill={colors.severity.medium}
              fillOpacity={fillOpacity}
            />
            <Area
              type="monotone"
              dataKey="low"
              stackId="1"
              stroke={colors.severity.low}
              fill={colors.severity.low}
              fillOpacity={fillOpacity}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="trend-chart-legend">
        <span className="legend-item">
          <span className="legend-dot" style={{ background: colors.severity.critical }}></span>
          Critical
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ background: colors.severity.high }}></span>
          High
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ background: colors.severity.medium }}></span>
          Medium
        </span>
        <span className="legend-item">
          <span className="legend-dot" style={{ background: colors.severity.low }}></span>
          Low
        </span>
      </div>
    </div>
  );
}

export default VulnTrendChart;
